import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { getPlayerControls, useIsWaveformProgress } from '../../../../helpers';
import { Buttons } from '../../../buttons';
import { Description, Image, TrackInfo } from '../../../common';
import { useSource } from '../../../sources';
import { usePlayer } from '../../PlayerProvider';
import { FullPlayControls, ProgressBar, Waveform } from '../../player-parts';
import { Playlist } from '../../playlist';

const FULL_IMAGE_SIZE = 168;
const SMALL_IMAGE_SIZE = 96;

export function Full() {
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerRef] = useRect(({ width }) => setContainerWidth(width), {
    resize: true
  });
  const { items, currentItem } = useSource();
  const {
    playerParts,
    playlistHeight,
    coverImage,
    controls: derivedControls,
    existingPlayerControls,
    playerControls,
    sourceType
  } = useSettings();
  const { showPlaylist } = usePlayer();
  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );

  const {
    image,
    title,
    description,
    hasAuthor,
    hasCaption,
    hasTitle,
    buttons
  } = currentItem ?? {};
  const showDescription = playerParts.includes('description') && !!description;
  const withProgressBar =
    sourceType !== 'radio' && controls.includes('progressBar');
  const isWaveformProgress = useIsWaveformProgress();
  const withImage = playerParts.includes('image');
  const hasTrackInfo = hasAuthor || hasCaption || hasTitle;
  const withSmallImage =
    !hasTitle && !hasCaption && !playerParts.includes('status');
  const imageSize = withSmallImage ? SMALL_IMAGE_SIZE : FULL_IMAGE_SIZE;
  const centerActions =
    !hasTrackInfo && !playerParts.includes('status') && !withProgressBar;

  const { url } = coverImage ?? {};

  return (
    <Container
      ref={containerRef}
      _withBottomPadding={!showPlaylist || !items.length}
    >
      <Component>
        <InfoContainer
          _withImage={withImage}
          _imageSize={imageSize}
          _showDescription={showDescription}
          _withHalfRows={!withProgressBar && !isWaveformProgress}
        >
          <Show when={withImage}>
            <ImageContainer
              trackName={title}
              link={url || image}
              _size={imageSize}
            />
          </Show>

          <StyledTrackInfo
            style={{ justifyContent: withProgressBar ? 'start' : undefined }}
          />

          <Actions _center={centerActions && !isWaveformProgress}>
            <Show when={withProgressBar}>
              <Show
                when={isWaveformProgress}
                fallback={<StyledProgressBar _showImage={withImage} />}
              >
                <Waveform />
              </Show>
            </Show>

            <Controls _withMargin={!centerActions && !isWaveformProgress}>
              <FullPlayControls />
            </Controls>
          </Actions>
        </InfoContainer>
        {showDescription && <Description text={description} />}
      </Component>

      {!!buttons?.length && (
        <Component>
          <StyledButtons containerWidth={containerWidth} />
        </Component>
      )}

      <Component>
        <StyledPlaylist maxHeight={playlistHeight} hideDirection="toTop" />
      </Component>
    </Container>
  );
}

const Container = styled.div<{
  _withBottomPadding?: boolean;
}>`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;

  ${({ _withBottomPadding }) =>
    _withBottomPadding &&
    css`
      & > :last-child {
        padding-bottom: 8px;
      }
    `}
`;

const Component = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 20px 20px 0;
  position: relative;
  max-width: 100%;

  &:not(:first-child) {
    padding-top: 0;
  }
`;

const StyledButtons = styled(Buttons)`
  padding-bottom: 12px;
`;

const StyledPlaylist = styled(Playlist)`
  margin: -6px 0 0 -6px;
`;

const InfoContainer = styled.div<{
  _withImage: boolean;
  _imageSize: number;
  _showDescription: boolean;
  _withHalfRows: boolean;
}>`
  padding-bottom: ${({ _showDescription }) => (_showDescription ? 20 : 12)}px;

  ${({ _withImage, _imageSize, _withHalfRows }) =>
    _withImage
      ? css`
          display: grid;
          column-gap: 20px;
          grid-auto-rows: ${_withHalfRows ? '50%' : 'auto'};
          grid-template-columns: ${_imageSize}px 1fr;
        `
      : css`
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
`;

const ImageContainer = styled(Image)<{
  _size: number;
}>`
  flex-shrink: 0;
  border-radius: 6px;
  position: relative;
  grid-area: span 2 / 1;
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;
`;

const StyledTrackInfo = styled(TrackInfo)`
  grid-area: 1 / 2;
`;

const Actions = styled.div<{
  _center: boolean;
}>`
  grid-column: 2;
  display: flex;
  flex-direction: column;

  ${({ _center }) => {
    if (_center) {
      return css`
        grid-row: span 2;
        margin: auto 0;
      `;
    }

    return css`
      grid-row: 2;
      margin-top: auto;
    `;
  }}
`;

const Controls = styled.div<{
  _withMargin: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ _withMargin }) => (_withMargin ? '12px' : 0)};
`;

const StyledProgressBar = styled(ProgressBar)<{
  _showImage: boolean;
}>`
  margin-top: ${({ _showImage }) => (_showImage ? 'auto' : '32px')};
`;
